import { Config } from 'common/config'
import Logger from 'common/logger'

const logger = new Logger('AWAITED_CONNECTIONS')

type AwaitedConnection = {
	promise: Promise<any>
	warningTimeoutId: number
}

export type Key = string

export enum ConnectionQuality {
	Good,
	Bad,
}

export type ConnectionQualityObserver = (quality: ConnectionQuality) => void
const awaitedConnections: Record<Key, AwaitedConnection> = {}
const warnedRequestKeys: Set<Key> = new Set<Key>()
let connectionQualityObserver: ConnectionQualityObserver | undefined = undefined

const notifyConnectionQualityObserver = (quality: ConnectionQuality) => {
	if (!connectionQualityObserver) {
		throw Error('No ConnectionQualityObserver has been registered')
	}
	connectionQualityObserver(quality)
}

const warn = (key: Key) => {
	if (warnedRequestKeys.size === 0) {
		// Naming the request matters: "Bad" on its own gives whoever is looking
		// at a stuck "network issues" screen nothing to go on.
		logger.warn(`Connection status: Bad (waiting on ${key})`)
		notifyConnectionQualityObserver(ConnectionQuality.Bad)
	}
	warnedRequestKeys.add(key)
}

const unwarn = (key: Key) => {
	if (warnedRequestKeys.delete(key) && warnedRequestKeys.size === 0) {
		logger.warn('Connection status: Good')
		notifyConnectionQualityObserver(ConnectionQuality.Good)
	}
}

export const awaitConnection = (
	key: Key,
	promise: Promise<any>
): Promise<any> => {
	awaitedConnections[key] = {
		promise,
		warningTimeoutId: window.setTimeout(
			() => warn(key),
			Config.requestExecutionTimeBeforeWarning
		),
	}
	/**
	 * Clear on **settled**, not on fulfilled, so a promise that rejects cannot
	 * leave its key in `warnedRequestKeys` and latch `ConnectionQuality` on
	 * `Bad`. The rejection itself is left to propagate unchanged.
	 *
	 * Today's callers never reject: `fetchReliably` and the image preload
	 * retry until they succeed, so their warning lasts exactly as long as the
	 * request is still failing - which is the point of the warning. The
	 * in-person "network issues" screen that first pointed here was a render
	 * crash shown by the error boundary, whose copy blames the network; it was
	 * not this.
	 */
	return promise.finally(() => clearAwaitedConnection(key))
}

export const onConnectionQualityChange = (
	observer: ConnectionQualityObserver
) => {
	if (connectionQualityObserver) {
		throw Error('Do not register multiple ConnectionQualityObservers!')
	}
	connectionQualityObserver = observer
}

export const removeConnectionQualityObserver = (
	observer: ConnectionQualityObserver
) => {
	if (connectionQualityObserver !== observer) {
		throw Error(
			'The quality change observer being removed is different from the one registered!'
		)
	}
	connectionQualityObserver = undefined
}

const clearAwaitedConnection = (key: Key) => {
	// Now reachable twice for one key - a settled promise clears itself, and a
	// retry can replace the entry under the same key first - so the entry may
	// already be gone. `unwarn` is still called either way: it is what releases
	// the Bad verdict, and it is idempotent.
	const awaited = awaitedConnections[key]
	if (awaited) {
		window.clearTimeout(awaited.warningTimeoutId)
		delete awaitedConnections[key]
	}
	unwarn(key)
}

export const getAwaitedPromise = (key: Key): Promise<any> | undefined => {
	return awaitedConnections[key]?.promise
}
